import MandatesRepository from "@/core/customers/repositories/MandatesRepository";
import {
  CreateCustomerMandatePayload,
  CustomerMandate,
  MandateStatus,
} from "@/core/customers/entities/CustomerMandate";
import { HttpClient } from "../../http/HttpClient";
import { ApiResponse } from "../../api/types/ApiResponse";
import { ApiResponseHandler } from "../../http/services/handleApiResponse/handleApiResponse";
import handleRequestError from "../../http/services/handleRequestError/handleRequestError";
import {
  createCustomerMandateUrl,
  getCustomerMandatesUrl,
  updateCustomerMandateUrl,
  deleteCustomerMandateUrl,
} from "../../api/customersBackendEndpoints/customersBackendEndpoints";

interface ApiCustomerMandate {
  mandateId?: string;
  role: CustomerMandate["role"];
  lastName: string;
  firstName: string;
  phone?: string | null;
  reason?: string | null;
  status?: MandateStatus;
  existingCustomerId?: string;
  mandataireCustomerId?: string;
}

const mapApiMandate = (item: ApiCustomerMandate): CustomerMandate => ({
  id: item.mandateId ?? "",
  role: item.role,
  lastName: item.lastName,
  firstName: item.firstName,
  phone: item.phone ?? "",
  reason: item.reason ?? "",
  status: item.status ?? "ACTIVE",
  existingCustomerId: item.existingCustomerId ?? item.mandataireCustomerId,
});

export default class MandatesRepositoryBackend implements MandatesRepository {
  constructor(private readonly httpClient: HttpClient) {}

  async getMandates(businessKey: string): Promise<CustomerMandate[]> {
    try {
      const response = await this.httpClient.get<
        ApiResponse<ApiCustomerMandate[] | { mandates?: ApiCustomerMandate[] }>
      >({
        url: getCustomerMandatesUrl(businessKey),
        withAuth: true,
      });

      const result = ApiResponseHandler.handleResponse(response);
      if (!result.success) {
        throw ApiResponseHandler.createExceptionFromResponse(response);
      }

      const raw = result.data;
      if (Array.isArray(raw)) {
        return raw.map(mapApiMandate);
      }
      if (raw && Array.isArray(raw.mandates)) {
        return raw.mandates.map(mapApiMandate);
      }
      return [];
    } catch (error) {
      throw handleRequestError(error as ApiResponse<unknown>);
    }
  }

  async createMandate(
    businessKey: string,
    payload: CreateCustomerMandatePayload,
  ): Promise<CustomerMandate> {
    try {
      const response = await this.httpClient.post<
        ApiResponse<ApiCustomerMandate>,
        CreateCustomerMandatePayload
      >({
        url: createCustomerMandateUrl(businessKey),
        body: payload,
        withAuth: true,
      });

      const result = ApiResponseHandler.handleResponse(response);
      if (!result.success || !result.data) {
        throw ApiResponseHandler.createExceptionFromResponse(response);
      }

      return mapApiMandate(result.data);
    } catch (error) {
      throw handleRequestError(error as ApiResponse<unknown>);
    }
  }

  async updateMandate(
    businessKey: string,
    mandateId: string,
    payload: CreateCustomerMandatePayload,
  ): Promise<CustomerMandate> {
    console.log("MandatesRepositoryBackend.updateMandate", { mandateId });
    try {
      const response = await this.httpClient.put<
        ApiResponse<ApiCustomerMandate>,
        CreateCustomerMandatePayload
      >({
        url: updateCustomerMandateUrl(businessKey, mandateId),
        body: payload,
        withAuth: true,
      });

      const result = ApiResponseHandler.handleResponse(response);
      if (!result.success || !result.data) {
        throw ApiResponseHandler.createExceptionFromResponse(response);
      }

      return mapApiMandate(result.data);
    } catch (error) {
      throw handleRequestError(error as ApiResponse<unknown>);
    }
  }

  async deleteMandate(
    businessKey: string,
    mandateId: string,
    reason: string,
  ): Promise<void> {
    console.log("MandatesRepositoryBackend.deleteMandate", { reason });
    try {
      const response = await this.httpClient.delete<ApiResponse<null>>({
        url: deleteCustomerMandateUrl(businessKey, mandateId, reason),
        withAuth: true,
      });

      const result = ApiResponseHandler.handleResponse(response);
      if (!result.success) {
        throw ApiResponseHandler.createExceptionFromResponse(response);
      }
    } catch (error) {
      throw handleRequestError(error as ApiResponse<unknown>);
    }
  }
}
